import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@tvg/fuse/button';
import saveForLaterService from 'services/save-for-later/save-for-later-service';
import sendAnalyticEvent from 'services/analytic/analytic-service';
import appWebhook from '@sd-utility/appwebhook';
import { IconFavoriteFilled, IconFavorite } from '@tvg/fuse/icon/all';
import eventBus from '@sd-utility/eventbus';
import { ProductVariant } from 'types/Product';
import LoggerFactory from 'utilities/loggerv2/loggerFactory';
import ErrorCodes from 'utilities/loggerv2/errorCodes';
import ErrorDialog from '../ErrorDialog/ErrorDialog';
import styles from './SaveForLater.module.css';

type Props = {
  productId: string;
  csrfToken: string;
  sessionConfirmationNumber: string;
  itemNumber: string;
  sku?: ProductVariant;
};

const loggerSFL = LoggerFactory.getLogger('Save for later component');

function SaveForLater(props: Props) {
  const { productId, csrfToken, sessionConfirmationNumber, itemNumber, sku } = props;
  const [isProductExist, setIsProductExists] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [dialogRendered, setDialogRendered] = useState(false);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (dialogRendered) {
      setHasError(true);
    }
  }, [dialogRendered]);

  const handleProductExistsCall = async () => {
    try {
      const isProductExisting = await saveForLaterService.checkProductExistsInSaveForLater(
        window,
        csrfToken,
        sessionConfirmationNumber,
        productId,
      );
      setIsProductExists(isProductExisting.productExists);
    } catch (error) {
      (await loggerSFL).logWithCode(ErrorCodes.SAVE_FOR_LATER_EXISTING_ITEM, error);
      console.error(error);
    }
  };

  useEffect(() => {
    if (sessionConfirmationNumber) {
      handleProductExistsCall();
    }
  }, [sessionConfirmationNumber]);

  const openErrorDialog = () => {
    if (dialogRendered) {
      setHasError(true);
    } else {
      setDialogRendered(true);
    }

    sendAnalyticEvent({
      gaAction: 'Save For Later',
      gaLabel: 'Ajax Failure - AddProduct',
    });
  };

  const handleAppWebHook = (isAdd: boolean) => appWebhook('saveForLater', { action: isAdd ? 'added' : 'removed' });

  const handleSaveForLaterToggle = async () => {
    try {
      if (isProductExist) {
        const removeFromSaveForLaterList = await saveForLaterService.removeProductToSaveForLater(
          window,
          csrfToken,
          sessionConfirmationNumber,
          productId,
        );
        if (removeFromSaveForLaterList) {
          handleAppWebHook(false);
          sendAnalyticEvent({
            gaAction: 'Click',
            gaLabel: 'Save for later - Remove',
          });

          eventBus.publish('wishlist/remove', {
            id: productId,
            itemNumber,
            sku: sku?.option,
          });

          setIsProductExists(false);
        }
      } else {
        const addToSaveForLaterList = await saveForLaterService.addProductToSaveForLater(
          window,
          csrfToken,
          sessionConfirmationNumber,
          productId,
        );
        if (addToSaveForLaterList) {
          handleAppWebHook(true);
          sendAnalyticEvent({
            gaAction: 'Click',
            gaLabel: 'Save for later',
          });

          eventBus.publish('wishlist/add', {
            id: productId,
            itemNumber,
            sku: sku?.option,
          });

          setIsProductExists(true);
        }
      }
    } catch (error) {
      (await loggerSFL).logWithCode(ErrorCodes.SAVE_FOR_LATER_SERVICE_ERROR, error);
      openErrorDialog();
    }
  };

  const closeErrorDialog = () => setHasError(false);

  const handleErrorCancel = () => {
    sendAnalyticEvent({
      gaAction: 'Save For Later - Error Dialog',
      gaLabel: 'Cancel',
    });
  };

  const handleErrorClose = () => {
    sendAnalyticEvent({
      gaAction: 'Save For Later - Error Dialog',
      gaLabel: 'Close',
    });
  };

  const handleErrorReload = () => {
    sendAnalyticEvent({
      gaAction: 'Save For Later - Error Dialog',
      gaLabel: 'Reload',
    });
  };

  return (
    <>
      <Button
        variant="tertiary"
        onClick={handleSaveForLaterToggle}
        data-cnstrc-btn="add_to_wishlist"
        data-testid="saved-for-later-icon-container"
        size="medium"
        fullWidth
        className={styles.button}
        icon={
          isProductExist ? (
            <IconFavoriteFilled
              height="20px"
              width="20px"
              title="filled heart"
              desc="filled heart for save for later"
            />
          ) : (
            <IconFavorite title="empty heart" desc="empty heart for save for later" height="20px" width="20px" />
          )
        }
      >
        {isProductExist ? 'Item saved' : 'Save item'}
      </Button>

      {dialogRendered && (
        <ErrorDialog
          open={hasError}
          closeDialog={closeErrorDialog}
          onCancelCallback={handleErrorCancel}
          onCloseCallback={handleErrorClose}
          onReloadCallback={handleErrorReload}
        />
      )}
    </>
  );
}

export default SaveForLater;
